// components/MultiPlatformPixelScript/tiktokPixel.ts — TikTok (ttq) pixel firing
import type { FireContext } from './types';
import { buildHashedUserDataForTikTokGoogle } from '../../utils/pixelPii';
import { getPersistentExternalId } from '../../services/attributionIds';
import { makeEmbedSkipper } from '../../utils/embedTracking';
import { mapPlatformEvent } from './eventMaps';

export function fireTiktokPixels(ctx: FireContext): void {
    const { configs, logPrefix, contentName, contentIds, orderData, value, userData, eventId, fireWithRetry, embedded, embedMode } = ctx;
    const skip = makeEmbedSkipper(embedded, embedMode, logPrefix);

    const tiktokConfigs = configs.filter(c => c.platform === 'tiktok');
    if (tiktokConfigs.length === 0) return;
    if (typeof (window as any).ttq === 'undefined') {
        (function (w, d, t) {
            (w as any).TiktokAnalyticsObject=t;var ttq=(w as any)[t]=(w as any)[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t: any,e: any){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t: any){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e: any,n: any){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript";o.async=true;o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode!.insertBefore(o,a)};
        })(window, document, 'ttq');
        console.log(`[${logPrefix} TikTok] Injected base SDK`);
    }

    const tiktokEventName = tiktokConfigs[0].eventName;
    // Hash DULU (await) baru track — identify selalu sebelum track (fix race: dulu
    // track fire duluan tanpa identify → event view 0% phone, EMQ 32).
    // external_id selalu ada (orderId di thanks / UUID persisten di form) supaya
    // browser↔CAPI bisa match + dedup walau form belum minta email.
    void (async () => {
        let hashed: Record<string, string> | null = null;
        try {
            const src = { ...(userData || {}), externalId: userData?.externalId || orderData?.orderId || getPersistentExternalId() };
            if (src.email || src.phone || src.name || src.city || src.province || src.externalId) {
                hashed = await buildHashedUserDataForTikTokGoogle(src);
            }
        } catch { hashed = null; }
        fireWithRetry(
            'TikTok',
            tiktokEventName,
            () => {
                const ttq = (window as any).ttq;
                return ttq && typeof ttq === 'object' && typeof ttq.instance === 'function';
            },
            () => {
                const ttq = (window as any).ttq;
                if (hashed && Object.keys(hashed).length) {
                    try {
                        const idPayload: Record<string, string> = {};
                        if (hashed.email) idPayload.email = hashed.email;
                        if (hashed.phone_number) idPayload.phone_number = hashed.phone_number;
                        if (hashed.external_id) (idPayload as any).external_id = hashed.external_id;
                        if (Object.keys(idPayload).length) { ttq.identify(idPayload); console.log(`[${logPrefix} TikTok] identify set`, Object.keys(idPayload)); }
                    } catch { /* */ }
                }

                tiktokConfigs.forEach(config => {
                    config.pixelIds.filter(id => id?.trim()).forEach(pixelId => {
                        try {
                            if (skip('TikTok', config.eventName)) return;
                            if (!ttq._i || !ttq._i[pixelId]) {
                                ttq.load(pixelId);
                                try { ttq.enableCookie && ttq.enableCookie(); } catch { /* */ }
                                if (!skip('TikTok', 'PageView')) ttq.page();
                            }

                            const params: any = {
                                content_name: contentName || 'Form',
                                currency: orderData?.currency || 'IDR',
                                content_type: 'product'
                            };
                            if (orderData?.contentIds?.length) params.content_id = orderData.contentIds[0];
                            else if (contentIds?.length) params.content_id = contentIds[0];

                            if (orderData) {
                                params.value = orderData.value;
                                params.order_id = orderData.orderId;
                                // event_id SAMA dgn CAPI (orderId) → dedup browser↔server (dulu
                                // browser tidak kirim event_id → double-count).
                                params.event_id = orderData.orderId;
                            } else {
                                if (value !== undefined) params.value = value;
                                // eventId eksplisit (IC/API non-purchase) → dedup dgn relay CAPI.
                                if (eventId) params.event_id = eventId;
                            }

                            const tiktokEvent = mapPlatformEvent('tiktok', config.eventName);

                            ttq.instance(pixelId).track(tiktokEvent, params);
                            console.log(`[${logPrefix} TikTok] ${tiktokEvent} FIRED for ${pixelId}!`);
                        } catch (e) {
                            console.error(`[${logPrefix} TikTok] Error:`, e);
                        }
                    });
                });
            }
        );
    })();
}
